import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Empty, Typography } from "antd";
import type { MerchantCabinetWithMerchantsType } from "@src/types";
import MerchantDescription from "./MerchantDescription";
import MerchantCabinetUpdateDrawer from "./drawers/MerchantCabinetUpdateDrawer";

const { Title, Text } = Typography;

export type MerchantCabinetDetailedCardProps = {
    data?: MerchantCabinetWithMerchantsType;
    loading?: boolean;
    initialMerchantId?: string;
};

export default function MerchantCabinetDetailedCard({
    data,
    loading,
    initialMerchantId,
}: MerchantCabinetDetailedCardProps) {
    const merchants = data?.merchants ?? [];

    const [activeTabKey, setActiveTabKey] = useState<string>(() => merchants[0]?.id ?? "");
    const [initialMerchantApplied, setInitialMerchantApplied] = useState(false);
    // ⬇️ состояние для Drawer
    const [updateOpen, setUpdateOpen] = useState(false);
    const openUpdate = useCallback(() => setUpdateOpen(true), []);
    const closeUpdate = useCallback(() => setUpdateOpen(false), []);

    useEffect(() => {
        setInitialMerchantApplied(false);
    }, [initialMerchantId]);

    useEffect(() => {
        if (!merchants.length) {
            setActiveTabKey("");
            return;
        }
        if (initialMerchantId && !initialMerchantApplied) {
            const existsInitial = merchants.some((m) => m.id === initialMerchantId);
            if (existsInitial) {
                setActiveTabKey(initialMerchantId);
                setInitialMerchantApplied(true);
                return;
            }
        }
        const exists = merchants.some((m) => m.id === activeTabKey);
        if (!exists) setActiveTabKey(merchants[0]?.id ?? "");
    }, [merchants, activeTabKey, initialMerchantId, initialMerchantApplied]);

    const tabList = useMemo(
        () =>
            merchants.map((m) => ({
                key: m.id,
                tab: (
                    <div className="flex items-center gap-2">
                        <span className="font-medium">{m.store_name || m.merchant_uid}</span>
                    </div>
                ),
            })),
        [merchants],
    );

    const contentList = useMemo<Record<string, React.ReactNode>>(() => {
        const map: Record<string, React.ReactNode> = {};
        for (const m of merchants) map[m.id] = <MerchantDescription key={m.id} data={m} />;
        return map;
    }, [merchants]);

    if (!loading && merchants.length === 0) {
        return (
            <Card title="Кабинет мерчанта" variant="outlined" style={{ width: "100%" }}>
                <Empty description="Нет мерчантов для этого кабинета" />
            </Card>
        );
    }

    return (
        <>
            <Card
                style={{ width: "100%" }}
                title={
                    <div className="flex flex-col">
                        <Title level={4} style={{ margin: 0 }}>
                            {data?.username}
                        </Title>
                        <Text type="secondary">Пароль: {data?.password}</Text>
                    </div>
                }
                loading={loading}
                tabList={tabList}
                activeTabKey={activeTabKey}
                onTabChange={setActiveTabKey}
                tabProps={{ size: "middle" }}
                extra={
                    <Button color="primary" variant="filled" onClick={openUpdate}>
                        Обновить пароль
                    </Button>
                }
            >
                {contentList[activeTabKey] ?? <Empty description="Выберите мерчанта" />}
            </Card>

            {/* Drawer обновления */}
            {data?.id && data?.username && (
                <MerchantCabinetUpdateDrawer
                    open={updateOpen}
                    onClose={closeUpdate}
                    cabinetId={data.id}
                    username={data.username}
                />
            )}
        </>
    );
}
